import type { Category, ConsentKitConfig, VendorDefinition } from "../types";

export interface BlocklistEntry {
  domain: string;
  category: Category;
  vendorId?: string;
}

/**
 * Decides, for a given URL, whether it may load yet.
 *
 * Mode semantics:
 *  - manual           only what the site declared: tagged elements, the vendors it
 *                     listed, and its own customBlocklist.
 *  - known-vendors    the above plus the mainstream tracker domains in the registry.
 *                     Default, because it cannot break payments, fonts or CDN scripts.
 *  - strict           the above plus the long tail (session recording, ad networks…).
 *  - all-third-party  anything cross-origin. Safest, breaks the most.
 */
export class Blocklist {
  private readonly entries: BlocklistEntry[];
  private readonly allowlist: string[];
  private readonly mode: ConsentKitConfig["blocking"];
  private readonly selfHost: string;

  constructor(config: ConsentKitConfig, vendors: VendorDefinition[], registry: VendorDefinition[]) {
    this.mode = config.blocking;
    this.allowlist = config.allowlist ?? [];
    this.selfHost = window.location.hostname;
    this.entries = buildEntries(config, vendors, registry, this.mode);
  }

  /** The category that must be granted before `url` may load, or null if it is free. */
  categoryFor(url: string): Category | null {
    const host = hostOf(url);
    if (!host) return null;
    if (host === this.selfHost && this.mode !== "all-third-party") return null;
    if (this.allowlist.some((domain) => matchesHost(host, domain))) return null;

    for (const entry of this.entries) {
      if (matchesHost(host, entry.domain)) return entry.category;
    }

    if (this.mode === "all-third-party" && host !== this.selfHost) return "marketing";
    return null;
  }

  /** Cookie-name patterns owned by vendors whose category is not granted. */
  cookiePatternsFor(vendors: VendorDefinition[]): Array<{ pattern: string; category: Category }> {
    return vendors.flatMap((vendor) =>
      vendor.cookies.map((cookie) => ({ pattern: cookie.name, category: vendor.category })),
    );
  }
}

function buildEntries(
  config: ConsentKitConfig,
  enabled: VendorDefinition[],
  registry: VendorDefinition[],
  mode: ConsentKitConfig["blocking"],
): BlocklistEntry[] {
  const entries: BlocklistEntry[] = [];
  const seen = new Set<string>();

  const push = (domain: string, category: Category, vendorId?: string) => {
    const key = `${domain}|${category}`;
    if (seen.has(key)) return;
    seen.add(key);
    entries.push({ domain, category, vendorId });
  };

  // A site's own customBlocklist wins over everything else, so it goes in first.
  for (const custom of config.customBlocklist ?? []) push(custom.domain, custom.category);

  // Vendors the site explicitly enabled are always blocked, in every mode.
  for (const vendor of enabled) {
    for (const domain of vendor.domains) push(domain, vendor.category, vendor.id);
  }

  if (mode === "manual") return entries;

  const includeStrictOnly = mode === "strict" || mode === "all-third-party";
  for (const vendor of registry) {
    if (vendor.strictOnly && !includeStrictOnly) continue;
    for (const domain of vendor.domains) push(domain, vendor.category, vendor.id);
  }

  return entries;
}

/** Handles absolute, protocol-relative and relative URLs alike. */
export function hostOf(url: string): string | null {
  if (!url) return null;
  const trimmed = url.trim();
  if (trimmed.startsWith("data:") || trimmed.startsWith("blob:") || trimmed.startsWith("javascript:")) {
    return null;
  }
  try {
    return new URL(trimmed, window.location.href).hostname;
  } catch {
    return null;
  }
}

function matchesHost(host: string, domain: string): boolean {
  const needle = domain.startsWith(".") ? domain.slice(1) : domain;
  return host === needle || host.endsWith(`.${needle}`);
}
